import { useMutation, useQueryClient, type Query } from "@tanstack/react-query";
import { rankingApi } from "@bw/shared/api";
import type { PlayerMatchResponse } from "@bw/shared/types/generated";
// MatchResultCreate is the request DTO the api client's reportResult expects
// (hand-written mirror in @bw/shared/types — winner_side strictly 1|2). Imported
// from the same barrel the client uses so the mutation arg type matches exactly.
import type { MatchResultCreate } from "@bw/shared/types";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Report a match result (POST /ranking/matches/report). The first report from a
 * side leaves the match `pending` (server stores winner_side + reported_by); the
 * SECOND report from the OPPOSING side agreeing flips it `completed`;
 * disagreement → `disputed`. The page surfaces whatever status the response
 * carries — never an invented "confirmed".
 *
 * No optimistic update: the cache is NOT mutated before the server confirms, so
 * a rejected report (403 not-a-participant / 409 already-finalized / network)
 * leaves no phantom state — the error surfaces on `mutation.error` for the page
 * to show honestly. On success the server response updates the existing match
 * query, then invalidation refreshes result and performance reads. Later query
 * responses remain authoritative; mutation data is not a second read store.
 */
export function useReportResult() {
  const queryClient = useQueryClient();

  return useMutation<
    PlayerMatchResponse,
    unknown,
    MatchResultCreate,
    Query | undefined
  >({
    mutationFn: (data: MatchResultCreate) => rankingApi.reportResult(data),
    onMutate: (data) =>
      queryClient.getQueryCache().find({
        queryKey: queryKeys.compete.match(data.match_id),
        exact: true,
      }),
    onSuccess: (result, _variables, originalQuery) => {
      const queryKey = queryKeys.compete.match(result.match_id);
      // An auth-boundary cache clear/replacement must not receive a late
      // response from the previous identity's mutation.
      if (
        originalQuery &&
        queryClient.getQueryCache().find({ queryKey, exact: true }) ===
          originalQuery
      ) {
        queryClient.setQueryData(queryKey, result);
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.compete.match(result.match_id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.compete.all,
      });
    },
  });
}
